import { CheckCircle2, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { markAppointmentCompleted } from '../api/appointments';
import { listInventoryItems } from '../api/inventory';
import { createPetMedicalRecord, uploadMedicalRecordAttachments } from '../api/pets';
import { getErrorMessage } from '../api/response';
import Button from './Button';
import FormField, { inputClassName } from './FormField';
import { dateTime, fromInputDateTime } from '../utils/format';
import { LoadingState } from './PageState';

const emptyForm = {
  diagnosis: '',
  clinical_details: '',
  treatments: '',
  weight: '',
  temperature: '',
  veterinarian_comments: '',
  next_control_at: '',
  next_control_notes: ''
};

const suggestedEvents = [
  { event_type: 'annual_health_control', title: 'Control anual de salud' },
  { event_type: 'rabies_vaccine', title: 'Vacuna antirrábica' },
  { event_type: 'internal_deworming', title: 'Desparasitación interna' },
  { event_type: 'external_deworming', title: 'Desparasitación externa' },
  { event_type: 'dental_control', title: 'Control dental' },
  { event_type: 'geriatric_control', title: 'Control geriátrico' },
  { event_type: 'feline_triple_vaccine', title: 'Vacuna triple felina' },
  { event_type: 'canine_octuple_vaccine', title: 'Vacuna óctuple canina' }
];

export default function AtencionModal({ appointment, records, loadingRecords, professionalId, professional, onClose, onSuccess }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [supplyItems, setSupplyItems] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [events, setEvents] = useState([]);
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    if (!appointment) return;

    listInventoryItems({ type: 'supply', status: 'active', limit: 200 })
      .then(setSupplyItems)
      .catch((error) => toast.error(getErrorMessage(error, 'No se pudieron cargar los insumos')));
  }, [appointment]);

  const supplyOptions = useMemo(() => {
    return supplyItems.filter((item) => Number(item.current_stock || 0) > 0);
  }, [supplyItems]);

  if (!appointment) return null;

  const resetAndClose = () => {
    setForm(emptyForm);
    setSupplies([]);
    setEvents([]);
    setAttachments([]);
    onClose?.();
  };

  const updateSupply = (index, field, value) => {
    setSupplies((current) => current.map((supply, supplyIndex) => (
      supplyIndex === index ? { ...supply, [field]: value } : supply
    )));
  };

  const updateEvent = (index, field, value) => {
    setEvents((current) => current.map((event, eventIndex) => (
      eventIndex === index ? { ...event, [field]: value } : event
    )));
  };

  const submitAttention = async (event) => {
    event.preventDefault();

    if (!appointment?.pet_id) {
      toast.error('Selecciona una cita con mascota asociada');
      return;
    }

    if (!form.diagnosis.trim() && !form.clinical_details.trim()) {
      toast.error('Ingresa diagnóstico o detalle clínico');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        description: form.diagnosis || form.clinical_details,
        diagnosis: form.diagnosis,
        clinical_details: form.clinical_details,
        treatments: form.treatments,
        veterinarian_comments: form.veterinarian_comments,
        weight: form.weight ? Number(form.weight) : null,
        temperature: form.temperature ? Number(form.temperature) : null,
        next_control_at: form.next_control_at ? fromInputDateTime(form.next_control_at) : null,
        next_control_notes: form.next_control_notes,
        next_control_title: 'Siguiente visita sugerida',
        appointment_id: appointment.id,
        appointment_starts_at: appointment.starts_at,
        service_id: appointment.service_id || null,
        service_name: appointment.service_name || null,
        professional_id: appointment.professional_id || Number(professionalId),
        professional_name: appointment.professional_name || professional?.full_name || null,
        supplies: supplies
          .filter((supply) => supply.supply_id && Number(supply.quantity || 0) > 0)
          .map((supply) => ({
            supply_id: Number(supply.supply_id),
            quantity: Number(supply.quantity || 0),
            notes: supply.notes || null
          })),
        events: events
          .filter((item) => item.title && item.event_date)
          .map((item) => ({
            event_type: item.event_type || 'other',
            title: item.title,
            description: item.description || null,
            event_date: item.event_date,
            notification_date: item.notification_date || null
          })),
        metadata: {
          source: 'admin_attention',
          appointment_id: appointment.id,
          appointment_starts_at: appointment.starts_at,
          service_id: appointment.service_id || null,
          service_name: appointment.service_name || null,
          professional_id: appointment.professional_id || Number(professionalId),
          professional_name: appointment.professional_name || professional?.full_name || null
        }
      };

      const record = await createPetMedicalRecord(appointment.pet_id, payload);

      if (attachments.length > 0) {
        await uploadMedicalRecordAttachments(record.id, attachments);
      }

      await markAppointmentCompleted(appointment.id);

      toast.success('Atención guardada y cita marcada como atendida');
      onSuccess?.();
      resetAndClose();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo guardar la atención'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-lg border border-blue-200 bg-white shadow-sm">
      <div className="flex flex-col gap-3 border-b border-gray-200 px-5 py-4 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Ficha clínica</p>
          <h2 className="text-xl font-semibold text-gray-950">
            Atender a {appointment.pet_name || `Mascota #${appointment.pet_id}`}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Cliente: {appointment.customer_name || `#${appointment.customer_id}`} · Hora: {dateTime(appointment.starts_at)}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Servicio: {appointment.service_name || 'Atención clínica'} · Profesional: {appointment.professional_name || professional?.full_name || 'Equipo clínico'}
          </p>
        </div>
        <Button type="button" variant="secondary" icon={X} onClick={resetAndClose} disabled={saving}>
          Cerrar ficha
        </Button>
      </div>

      <div className="grid gap-6 p-5 xl:grid-cols-[minmax(0,1.3fr)_minmax(360px,0.7fr)]">
        <form id="attention-form" className="space-y-5" onSubmit={submitAttention}>
          <section className="grid gap-4 lg:grid-cols-2">
            <FormField label="Diagnóstico" required>
              <textarea
                rows={5}
                className={inputClassName}
                value={form.diagnosis}
                onChange={(event) => setForm({ ...form, diagnosis: event.target.value })}
                required
              />
            </FormField>

            <FormField label="Detalle clínico">
              <textarea
                rows={5}
                className={inputClassName}
                value={form.clinical_details}
                onChange={(event) => setForm({ ...form, clinical_details: event.target.value })}
              />
            </FormField>
          </section>

          <FormField label="Tratamiento / indicaciones">
            <textarea
              rows={4}
              className={inputClassName}
              value={form.treatments}
              onChange={(event) => setForm({ ...form, treatments: event.target.value })}
            />
          </FormField>

          <div className="grid gap-3 md:grid-cols-3">
            <FormField label="Peso">
              <input
                type="number"
                min="0"
                step="0.01"
                className={inputClassName}
                value={form.weight}
                onChange={(event) => setForm({ ...form, weight: event.target.value })}
                placeholder="Kg"
              />
            </FormField>
            <FormField label="Temperatura">
              <input
                type="number"
                min="0"
                step="0.1"
                className={inputClassName}
                value={form.temperature}
                onChange={(event) => setForm({ ...form, temperature: event.target.value })}
                placeholder="°C"
              />
            </FormField>
            <FormField label="Siguiente control">
              <input
                type="datetime-local"
                className={inputClassName}
                value={form.next_control_at}
                onChange={(event) => setForm({ ...form, next_control_at: event.target.value })}
              />
            </FormField>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <FormField label="Comentarios del veterinario">
              <textarea
                rows={4}
                className={inputClassName}
                value={form.veterinarian_comments}
                onChange={(event) => setForm({ ...form, veterinarian_comments: event.target.value })}
              />
            </FormField>
            <FormField label="Nota de recordatorio">
              <textarea
                rows={4}
                className={inputClassName}
                value={form.next_control_notes}
                onChange={(event) => setForm({ ...form, next_control_notes: event.target.value })}
                placeholder="Control, vacuna, examen..."
              />
            </FormField>
          </div>

          <section className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div>
                <h3 className="font-semibold text-gray-950">Insumos utilizados</h3>
                <p className="text-xs text-gray-500">Se descontarán de bodega al guardar la atención.</p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                icon={Plus}
                onClick={() => setSupplies((current) => [...current, { supply_id: '', quantity: 1, notes: '' }])}
              >
                Insumo
              </Button>
            </div>
            {supplies.length === 0 ? (
              <p className="rounded-md border border-dashed border-gray-300 bg-white p-4 text-sm text-gray-500">Sin insumos registrados.</p>
            ) : (
              <div className="space-y-3">
                {supplies.map((supply, index) => (
                  <div key={index} className="grid gap-3 rounded-md border border-gray-200 bg-white p-3 md:grid-cols-[1fr_120px_1fr_auto] md:items-end">
                    <FormField label="Insumo">
                      <select className={inputClassName} value={supply.supply_id} onChange={(event) => updateSupply(index, 'supply_id', event.target.value)}>
                        <option value="">Selecciona</option>
                        {supplyOptions.map((item) => (
                          <option key={item.id} value={item.id}>{item.name} · stock {Number(item.current_stock || 0)} {item.unit}</option>
                        ))}
                      </select>
                    </FormField>
                    <FormField label="Cantidad">
                      <input className={inputClassName} type="number" min="0.01" step="0.01" value={supply.quantity} onChange={(event) => updateSupply(index, 'quantity', event.target.value)} />
                    </FormField>
                    <FormField label="Nota">
                      <input className={inputClassName} value={supply.notes} onChange={(event) => updateSupply(index, 'notes', event.target.value)} />
                    </FormField>
                    <Button
                      type="button"
                      variant="ghost"
                      icon={Trash2}
                      onClick={() => setSupplies((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                    >
                      Quitar
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div>
                <h3 className="font-semibold text-gray-950">Eventos futuros de la mascota</h3>
                <p className="text-xs text-gray-500">Estos eventos quedan listos para cron de recordatorios.</p>
              </div>
              <select
                className={`${inputClassName} max-w-xs`}
                value=""
                onChange={(event) => {
                  const selected = suggestedEvents.find((item) => item.event_type === event.target.value);
                  if (selected) {
                    setEvents((current) => [...current, { ...selected, event_date: '', notification_date: '', description: '' }]);
                  }
                }}
              >
                <option value="">Agregar sugerido</option>
                {suggestedEvents.map((item) => <option key={item.event_type} value={item.event_type}>{item.title}</option>)}
              </select>
            </div>
            {events.length === 0 ? (
              <p className="rounded-md border border-dashed border-gray-300 bg-white p-4 text-sm text-gray-500">Sin eventos futuros.</p>
            ) : (
              <div className="space-y-3">
                {events.map((item, index) => (
                  <div key={index} className="grid gap-3 rounded-md border border-gray-200 bg-white p-3 md:grid-cols-[1fr_180px_180px_auto] md:items-end">
                    <FormField label="Evento">
                      <input className={inputClassName} value={item.title} onChange={(event) => updateEvent(index, 'title', event.target.value)} />
                    </FormField>
                    <FormField label="Fecha evento">
                      <input className={inputClassName} type="date" value={item.event_date} onChange={(event) => updateEvent(index, 'event_date', event.target.value)} />
                    </FormField>
                    <FormField label="Fecha notificación">
                      <input className={inputClassName} type="date" value={item.notification_date} onChange={(event) => updateEvent(index, 'notification_date', event.target.value)} />
                    </FormField>
                    <Button type="button" variant="ghost" icon={Trash2} onClick={() => setEvents((current) => current.filter((_, eventIndex) => eventIndex !== index))}>
                      Quitar
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <FormField label="Adjuntos PDF / imagen">
              <input
                className={inputClassName}
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp"
                multiple
                onChange={(event) => setAttachments(Array.from(event.target.files || []))}
              />
            </FormField>
            <p className="mt-2 text-xs text-gray-500">Máximo 10 archivos por ficha. Tamaño máximo recomendado: 5 MB por archivo.</p>
            {attachments.length > 0 && (
              <ul className="mt-3 space-y-1 text-xs text-gray-600">
                {attachments.map((file) => <li key={file.name}>{file.name}</li>)}
              </ul>
            )}
          </section>

          <div className="sticky bottom-0 flex flex-col-reverse gap-2 border-t border-gray-200 bg-white py-4 sm:flex-row sm:justify-end">
            <Button type="button" variant="secondary" onClick={resetAndClose} disabled={saving}>Cancelar</Button>
            <Button type="submit" icon={CheckCircle2} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar atención'}
            </Button>
          </div>
        </form>

        <aside className="space-y-4">
          <section className="rounded-lg border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-950">Datos de atención</h3>
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between gap-4"><dt className="text-gray-500">Mascota</dt><dd className="font-medium text-gray-950">{appointment.pet_name || `#${appointment.pet_id}`}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-gray-500">Cliente</dt><dd className="font-medium text-gray-950">{appointment.customer_name || `#${appointment.customer_id}`}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-gray-500">Servicio</dt><dd className="font-medium text-gray-950">{appointment.service_name || '-'}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-gray-500">Hora</dt><dd className="font-medium text-gray-950">{dateTime(appointment.starts_at)}</dd></div>
            </dl>
          </section>

          <section className="rounded-lg border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-950">Historial clínico</h3>
            {loadingRecords ? (
              <LoadingState rows={5} />
            ) : records.length ? (
              <div className="mt-3 max-h-[620px] space-y-3 overflow-y-auto">
                {records.map((record) => (
                  <article key={record.id} className="rounded-md border border-gray-200 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-semibold text-gray-950">{record.diagnosis || record.description}</p>
                      <span className="whitespace-nowrap text-xs text-gray-500">{dateTime(record.created_at)}</span>
                    </div>
                    {record.treatments && <p className="mt-2 text-xs text-gray-600">{record.treatments}</p>}
                    {(record.weight || record.temperature) && (
                      <p className="mt-2 text-xs text-gray-500">
                        {record.weight && `Peso: ${record.weight} kg`}
                        {record.weight && record.temperature && ' · '}
                        {record.temperature && `Temp: ${record.temperature}°C`}
                      </p>
                    )}
                    {record.files?.length > 0 && <p className="mt-2 text-xs text-blue-700">{record.files.length} adjunto(s)</p>}
                    {record.events?.length > 0 && <p className="mt-1 text-xs text-green-700">{record.events.length} evento(s) futuro(s)</p>}
                    {record.supplies?.length > 0 && <p className="mt-1 text-xs text-purple-700">{record.supplies.length} insumo(s)</p>}
                  </article>
                ))}
              </div>
            ) : (
              <p className="mt-3 rounded-md border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500">
                Esta mascota aún no tiene atenciones registradas.
              </p>
            )}
          </section>
        </aside>
      </div>
    </section>
  );
}
